import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  Send, LogOut, User, Plus, Trash2, Menu, Mic, Image as ImageIcon, 
  Paperclip, Video, Mail, MessageSquare, FileText, TrendingUp, 
  Users, Calendar, Database, Briefcase, Settings, Bell, Search,
  DollarSign, Award, BookOpen, Phone, Zap, BarChart, Globe,
  ChevronDown, CreditCard, Shield, HelpCircle, Clock, Target,
  Folder, PieChart, Activity, Layers, GitBranch, Package,
  Clipboard, CheckSquare, List, Download, Upload, Share2,
  Lock, Key, AlertCircle, Coffee, Sparkles, Star, Crown, Sun, Moon
} from 'lucide-react';

const API_URL = window.location.origin;

export default function Chat({ username, onLogout }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  const [chatList, setChatList] = useState([]);
  const [currentChatId, setCurrentChatId] = useState(null);
  const [showFeaturesMenu, setShowFeaturesMenu] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(true);
  const messagesEndRef = useRef(null);
  const userDropdownRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    loadChats();
    // Load theme preference
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'light') {
      setIsDarkMode(false);
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleTheme = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    localStorage.setItem('theme', newMode ? 'dark' : 'light');
  };

  const theme = isDarkMode ? {
    // Dark theme
    bg: 'bg-[#0a0e1a]',
    headerBg: 'bg-[#0f1419]',
    sidebarBg: 'bg-[#0f1419]',
    cardBg: 'bg-[#1a2332]',
    cardBgHover: 'bg-[#242f42]',
    actionBg: 'bg-[#131920]',
    inputBg: 'bg-[#1a2332]',
    border: 'border-gray-800',
    borderLight: 'border-gray-700',
    text: 'text-white',
    textSecondary: 'text-gray-300',
    textMuted: 'text-gray-400',
    textDim: 'text-gray-500',
    hoverBg: 'hover:bg-gray-800',
    hoverText: 'hover:text-white',
    accent: 'text-blue-400',
    accentBg: 'bg-blue-600'
  } : {
    // Light theme
    bg: 'bg-gray-50',
    headerBg: 'bg-white',
    sidebarBg: 'bg-white',
    cardBg: 'bg-gray-100',
    cardBgHover: 'bg-gray-200',
    actionBg: 'bg-gray-100',
    inputBg: 'bg-white',
    border: 'border-gray-200',
    borderLight: 'border-gray-300',
    text: 'text-gray-900',
    textSecondary: 'text-gray-700',
    textMuted: 'text-gray-600',
    textDim: 'text-gray-500',
    hoverBg: 'hover:bg-gray-100',
    hoverText: 'hover:text-gray-900',
    accent: 'text-blue-600',
    accentBg: 'bg-blue-600'
  };

  const loadChats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/chats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChatList(response.data.chats || []);
    } catch (err) {
      console.error('Failed to load chats:', err);
    }
  };

  const loadChatMessages = async (chatId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/chats/${chatId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const formattedMessages = response.data.messages.map(msg => [
        { type: 'user', text: msg.question },
        { type: 'ai', text: msg.answer }
      ]).flat();
      
      setMessages(formattedMessages);
      setCurrentChatId(chatId);
    } catch (err) {
      console.error('Failed to load chat messages:', err);
    }
  };

  const handleNewChat = () => {
    setMessages([]);
    setCurrentChatId(null);
    setInput('');
  };

  const handleSelectChat = (chatId) => {
    loadChatMessages(chatId);
  };

  const handleDeleteChat = async (chatId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this chat?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/chats/${chatId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (currentChatId === chatId) {
        handleNewChat();
      }
      loadChats();
    } catch (err) {
      console.error('Failed to delete chat:', err);
    }
  };

  const showUpgradePopup = (feature) => {
    setUpgradeFeature(feature);
    setShowUpgradeModal(true);
  };

  const handleUpgradeRedirect = () => {
    window.open('https://oawo.co.uk/en/page/ai-model-training', '_blank');
    setShowUpgradeModal(false);
  };

  const quickActions = [
    { icon: DollarSign, label: 'Salary Info' },
    { icon: Award, label: 'Promotion' },
    { icon: Calendar, label: 'Leave' },
    { icon: Mail, label: 'Email' },
    { icon: Phone, label: 'WhatsApp' },
    { icon: Video, label: 'Video Call' },
    { icon: BarChart, label: 'Analytics' },
    { icon: Users, label: 'Team' }
  ];

  const advancedFeatures = [
    { icon: FileText, label: 'Document Analysis', desc: 'AI-powered document insights', category: 'Documents' },
    { icon: TrendingUp, label: 'Data Analytics', desc: 'Smart data visualization', category: 'Analytics' },
    { icon: Briefcase, label: 'Company Model', desc: 'Your tailored AI assistant', category: 'AI' },
    { icon: Database, label: 'Knowledge Base', desc: 'Search company docs', category: 'Documents' },
    { icon: BookOpen, label: 'Training Hub', desc: 'Learning & development', category: 'HR' },
    { icon: Zap, label: 'Automation', desc: 'Automate workflows', category: 'Productivity' },
    { icon: Globe, label: 'Multi-language', desc: 'Support 50+ languages', category: 'AI' },
    { icon: Settings, label: 'Integrations', desc: 'Connect your tools', category: 'Settings' }
  ];

  const userMenuSections = [
    {
      title: 'My Workspace',
      items: [
        { icon: User, label: 'My Profile', desc: 'Personal information' },
        { icon: Folder, label: 'My Documents', desc: 'Files & folders' },
        { icon: Clock, label: 'Activity History', desc: 'Recent actions' },
        { icon: Star, label: 'Favorites', desc: 'Saved items' }
      ]
    },
    {
      title: 'HR & Career',
      items: [
        { icon: DollarSign, label: 'Salary & Benefits', desc: 'Compensation details' },
        { icon: Award, label: 'Performance', desc: 'Reviews & goals' },
        { icon: Target, label: 'Career Path', desc: 'Development plan' },
        { icon: Calendar, label: 'Time Off', desc: 'Leave management' }
      ]
    },
    {
      title: 'Collaboration',
      items: [
        { icon: Users, label: 'My Team', desc: 'Team members' },
        { icon: MessageSquare, label: 'Messages', desc: 'Internal chat' },
        { icon: Video, label: 'Meetings', desc: 'Scheduled calls' },
        { icon: Share2, label: 'Shared With Me', desc: 'Collaboration' }
      ]
    },
    {
      title: 'Analytics & Reports',
      items: [
        { icon: BarChart, label: 'Dashboard', desc: 'Personal metrics' },
        { icon: PieChart, label: 'Reports', desc: 'Data insights' },
        { icon: Activity, label: 'Performance', desc: 'KPI tracking' },
        { icon: TrendingUp, label: 'Goals', desc: 'OKR progress' }
      ]
    },
    {
      title: 'Tools & Integrations',
      items: [
        { icon: Mail, label: 'Email', desc: 'Connect email' },
        { icon: Phone, label: 'WhatsApp', desc: 'Messaging' },
        { icon: Database, label: 'Databases', desc: 'Data sources' },
        { icon: GitBranch, label: 'Version Control', desc: 'Git integration' }
      ]
    },
    {
      title: 'Settings & Security',
      items: [
        { icon: Settings, label: 'Preferences', desc: 'App settings' },
        { icon: Shield, label: 'Privacy', desc: 'Data protection' },
        { icon: Lock, label: 'Security', desc: 'Authentication' },
        { icon: Key, label: 'API Keys', desc: 'Integration keys' }
      ]
    },
    {
      title: 'Resources',
      items: [
        { icon: BookOpen, label: 'Documentation', desc: 'User guides' },
        { icon: HelpCircle, label: 'Help Center', desc: 'Support' },
        { icon: Coffee, label: 'Whats New', desc: 'Latest updates' },
        { icon: Crown, label: 'Upgrade Plan', desc: 'Premium features' }
      ]
    }
  ];

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { type: 'user', text: userMessage }]);
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/chat`,
        { 
          message: userMessage,
          chat_id: currentChatId 
        },
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 60000
        }
      );

      setMessages(prev => [...prev, { type: 'ai', text: response.data.answer }]);
      
      if (!currentChatId) {
        setCurrentChatId(response.data.chat_id);
        loadChats();
      }
    } catch (err) {
      setMessages(prev => [...prev, { 
        type: 'ai', 
        text: `❌ Error: ${err.response?.data?.detail || err.message || 'Please try again.'}` 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className={`flex flex-col h-screen ${theme.bg}`}>
      {/* Header */}
      <header className={`h-16 flex items-center justify-between px-6 ${theme.headerBg} ${theme.border} border-b`}>
        <div className="flex items-center space-x-4">
          <button 
            onClick={() => setShowSidebar(!showSidebar)}
            className={`p-2 ${theme.textMuted} ${theme.hoverText} ${theme.hoverBg} transition`}
          >
            <Menu size={20} />
          </button>
          <div className="flex items-center space-x-3">
            <img src="/logo.png" alt="TwoRun.AI" className="h-10 object-contain bg-white p-1" />
            <div>
              <h1 className={`text-lg font-bold ${theme.text}`}>TwoRun.AI</h1>
              <p className={`text-xs ${theme.accent}`}>Enterprise Intelligence Platform</p>
            </div>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          {/* Theme Switcher */}
          <button
            onClick={toggleTheme}
            className={`p-2 ${theme.textMuted} ${theme.hoverText} ${theme.hoverBg} transition`}
            title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
          </button>

          <button
            onClick={() => showUpgradePopup('Notifications')}
            className={`relative p-2 ${theme.textMuted} ${theme.hoverText} ${theme.hoverBg} transition`}
          >
            <Bell size={20} />
            <span className="absolute top-1 right-1 w-2 h-2 bg-blue-500"></span>
          </button>
          
          <div className="relative" ref={userDropdownRef}>
            <button
              onClick={() => setShowUserDropdown(!showUserDropdown)}
              className={`flex items-center space-x-2 px-4 py-2 ${theme.cardBg} ${theme.text} ${theme.cardBgHover} transition border ${theme.borderLight}`}
            >
              <User size={16} />
              <span className="text-sm font-semibold">{username}</span>
              <ChevronDown size={16} />
            </button>

            {/* MEGA USER DROPDOWN */}
            {showUserDropdown && (
              <div className={`absolute right-0 mt-2 w-[800px] ${theme.headerBg} border ${theme.borderLight} shadow-2xl z-50 max-h-[600px] overflow-y-auto`}>
                <div className={`${theme.cardBg} p-4 border-b ${theme.borderLight}`}>
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-blue-600 flex items-center justify-center">
                      <User size={24} className="text-white" />
                    </div>
                    <div>
                      <h3 className={`font-bold ${theme.text} text-lg`}>{username}</h3>
                      <p className={`text-sm ${theme.accent}`}>Premium Member</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 p-4">
                  {userMenuSections.map((section, idx) => (
                    <div key={idx} className="space-y-2">
                      <h4 className={`text-xs font-bold ${theme.accent} uppercase tracking-wider px-2 py-1 ${theme.cardBg}`}>
                        {section.title}
                      </h4>
                      {section.items.map((item, itemIdx) => (
                        <button
                          key={itemIdx}
                          onClick={() => {
                            showUpgradePopup(item.label);
                            setShowUserDropdown(false);
                          }}
                          className={`w-full flex items-start space-x-2 p-2 ${theme.hoverBg} transition text-left group border border-transparent hover:border-blue-500`}
                        >
                          <div className={`p-1.5 ${theme.cardBg} ${theme.accent} group-hover:bg-blue-600 group-hover:text-white transition`}>
                            <item.icon size={16} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-semibold ${theme.textSecondary} ${theme.hoverText}`}>{item.label}</p>
                            <p className={`text-xs ${theme.textDim} truncate`}>{item.desc}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  ))}
                </div>

                <div className={`p-4 ${theme.cardBg} flex justify-between items-center border-t ${theme.borderLight}`}>
                  <button
                    onClick={() => showUpgradePopup('Premium Features')}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white font-bold hover:bg-blue-700 transition border border-blue-500"
                  >
                    <Crown size={18} />
                    <span>Upgrade to Premium</span>
                  </button>
                  <button
                    onClick={onLogout}
                    className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white font-bold hover:bg-red-700 transition border border-red-500"
                  >
                    <LogOut size={18} />
                    <span>Logout</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Quick Actions Bar */}
      <div className={`${theme.actionBg} px-6 py-3 overflow-x-auto border-b ${theme.border}`}>
        <div className="flex items-center space-x-2">
          <span className={`text-xs font-semibold ${theme.accent} mr-2`}>QUICK ACTIONS:</span>
          {quickActions.map((action, idx) => (
            <button
              key={idx}
              onClick={() => showUpgradePopup(action.label)}
              className={`flex items-center space-x-2 px-3 py-2 ${theme.cardBg} ${theme.textSecondary} ${theme.cardBgHover} ${theme.hoverText} transition-all text-xs font-medium whitespace-nowrap border ${theme.borderLight}`}
            >
              <action.icon size={14} />
              <span>{action.label}</span>
            </button>
          ))}
          
          <button
            onClick={() => setShowFeaturesMenu(!showFeaturesMenu)}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 transition-all text-xs font-bold border border-blue-500"
          >
            <Sparkles size={14} />
            <span>More Features</span>
          </button>
        </div>
      </div>

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className={`${theme.headerBg} w-full max-w-2xl shadow-2xl border ${theme.borderLight}`}>
            <div className={`${theme.cardBg} p-6 border-b ${theme.borderLight}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Crown className={`w-10 h-10 ${theme.accent}`} />
                  <div>
                    <h2 className={`text-2xl font-bold ${theme.text}`}>Upgrade Required</h2>
                    <p className={`text-sm ${theme.accent}`}>Unlock premium features</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowUpgradeModal(false)} 
                  className={`${theme.textMuted} ${theme.hoverText} ${theme.hoverBg} p-2 text-3xl font-bold`}
                >
                  ×
                </button>
              </div>
            </div>
            
            <div className="p-8">
              <div className={`${theme.cardBg} p-4 mb-6 border border-blue-500`}>
                <p className={`text-lg ${theme.text} font-semibold mb-2`}>
                  <AlertCircle className={`inline mr-2 ${theme.accent}`} size={24} />
                  Feature: <span className={theme.accent}>{upgradeFeature}</span>
                </p>
                <p className={`text-sm ${theme.textMuted}`}>
                  This premium feature is available in our Enterprise plan. Upgrade now to access advanced AI capabilities, integrations, and automation tools.
                </p>
              </div>

              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className={`text-center p-4 ${theme.cardBg} border ${theme.borderLight}`}>
                  <Database className={`w-8 h-8 ${theme.accent} mx-auto mb-2`} />
                  <h4 className={`font-bold text-sm ${theme.text}`}>AI Training</h4>
                  <p className={`text-xs ${theme.textMuted} mt-1`}>Custom models</p>
                </div>
                <div className={`text-center p-4 ${theme.cardBg} border ${theme.borderLight}`}>
                  <Zap className={`w-8 h-8 ${theme.accent} mx-auto mb-2`} />
                  <h4 className={`font-bold text-sm ${theme.text}`}>Automation</h4>
                  <p className={`text-xs ${theme.textMuted} mt-1`}>Smart workflows</p>
                </div>
                <div className={`text-center p-4 ${theme.cardBg} border ${theme.borderLight}`}>
                  <Users className={`w-8 h-8 ${theme.accent} mx-auto mb-2`} />
                  <h4 className={`font-bold text-sm ${theme.text}`}>Team Access</h4>
                  <p className={`text-xs ${theme.textMuted} mt-1`}>Collaboration</p>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={handleUpgradeRedirect}
                  className="flex-1 bg-blue-600 text-white px-6 py-4 font-bold text-lg hover:bg-blue-700 transition flex items-center justify-center space-x-2 border border-blue-500"
                >
                  <Crown size={24} />
                  <span>Request Enterprise Access</span>
                </button>
                <button
                  onClick={() => setShowUpgradeModal(false)}
                  className={`px-6 py-4 ${theme.cardBg} ${theme.textSecondary} font-bold ${theme.cardBgHover} ${theme.hoverText} transition border ${theme.borderLight}`}
                >
                  Maybe Later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Features Menu Modal */}
      {showFeaturesMenu && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowFeaturesMenu(false)}>
          <div className={`${theme.headerBg} max-w-5xl w-full shadow-2xl border ${theme.borderLight} max-h-[80vh] overflow-y-auto`} onClick={e => e.stopPropagation()}>
            <div className={`flex items-center justify-between p-6 ${theme.cardBg} border-b ${theme.borderLight}`}>
              <div>
                <h2 className={`text-2xl font-bold ${theme.text}`}>Advanced Features</h2>
                <p className={`text-sm ${theme.accent} mt-1`}>Discover enterprise capabilities</p>
              </div>
              <button onClick={() => setShowFeaturesMenu(false)} className={`p-2 ${theme.hoverBg} ${theme.textMuted} ${theme.hoverText} text-3xl font-bold`}>
                ×
              </button>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4 p-6">
              {advancedFeatures.map((feature, idx) => (
                <div
                  key={idx}
                  onClick={() => {
                    showUpgradePopup(feature.label);
                    setShowFeaturesMenu(false);
                  }}
                  className={`p-4 ${theme.cardBg} ${theme.cardBgHover} cursor-pointer transition-all group border ${theme.borderLight} hover:border-blue-500`}
                >
                  <div className="flex items-start space-x-3">
                    <div className="p-2 bg-blue-600 text-white group-hover:bg-blue-700 transition">
                      <feature.icon size={24} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h3 className={`font-bold ${theme.text} text-lg`}>{feature.label}</h3>
                        <span className="text-[10px] bg-blue-600 text-white px-2 py-1 font-semibold">{feature.category}</span>
                      </div>
                      <p className={`text-sm ${theme.textMuted} mt-1`}>{feature.desc}</p>
                      <span className={`inline-block mt-2 text-xs bg-blue-500/20 ${theme.accent} px-3 py-1 font-semibold border border-blue-500`}>Premium Feature</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        {showSidebar && (
          <aside className={`w-72 flex flex-col ${theme.sidebarBg} border-r ${theme.border}`}>
            <div className={`p-4 ${theme.actionBg}`}>
              <button
                onClick={handleNewChat}
                className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 text-white hover:bg-blue-700 transition-all shadow font-semibold border border-blue-500"
              >
                <Plus size={20} />
                <span>New Conversation</span>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              <div className="p-4">
                <h3 className={`text-xs font-bold ${theme.accent} uppercase tracking-wider mb-3 px-2 py-1 ${theme.cardBg}`}>Recent Chats</h3>
                
                {chatList.length === 0 ? (
                  <div className="text-center py-12">
                    <div className={`w-16 h-16 ${theme.cardBg} flex items-center justify-center mx-auto mb-3 border ${theme.borderLight}`}>
                      <MessageSquare size={24} className={theme.textDim} />
                    </div>
                    <p className={`text-sm ${theme.textMuted} font-semibold`}>No conversations yet</p>
                    <p className={`text-xs ${theme.textDim} mt-1`}>Start chatting to begin</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {chatList.map((chat) => (
                      <div
                        key={chat.id}
                        onClick={() => handleSelectChat(chat.id)}
                        className={`group relative p-3 cursor-pointer transition-all border ${
                          currentChatId === chat.id
                            ? `${theme.cardBg} border-blue-500`
                            : `${theme.border} ${theme.hoverBg} hover:border-gray-700`
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <p className={`text-sm font-medium ${theme.textSecondary} truncate pr-8 flex-1 leading-snug`}>
                            {chat.title}
                          </p>
                          <button
                            onClick={(e) => handleDeleteChat(chat.id, e)}
                            className={`absolute right-3 top-3 opacity-0 group-hover:opacity-100 ${theme.textDim} hover:text-red-400 transition p-1 hover:bg-red-900/20`}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                        <p className={`text-xs ${theme.textDim} mt-2 font-medium`}>
                          {new Date(chat.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className={`p-4 ${theme.cardBg} text-center border-t ${theme.border}`}>
              <p className={`text-xs ${theme.accent} font-bold`}>
                🚀 TwoRun.AI Enterprise
              </p>
            </div>
          </aside>
        )}

        {/* Main Chat Area */}
        <main className={`flex-1 flex flex-col ${theme.bg}`}>
          <div className="flex-1 overflow-y-auto p-6">
            <div className="max-w-4xl mx-auto space-y-6">
              {messages.length === 0 && (
                <div className="text-center py-20">
                  <img src="/logo.png" alt="Logo" className="w-24 h-24 mx-auto mb-6 object-contain bg-white p-2" />
                  <h2 className={`text-3xl font-bold ${theme.text} mb-3`}>
                    Welcome to Your AI Workspace
                  </h2>
                  <p className={`text-sm ${theme.textMuted} mb-8 max-w-2xl mx-auto font-medium`}>
                    Enterprise Intelligence Platform - Ask about salary, request leave, send emails, analyze data, or access company information
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl mx-auto">
                    {[
                      { icon: '💰', text: 'Salary Info', query: 'What is my current salary?' },
                      { icon: '📈', text: 'Career Growth', query: 'What do I need for promotion?' },
                      { icon: '🏖️', text: 'Request Leave', query: 'I need 3 days leave' },
                      { icon: '📧', text: 'Send Email', query: 'Send email to manager' },
                      { icon: '📊', text: 'Analytics', query: 'Show performance metrics' },
                      { icon: '👥', text: 'Team Info', query: 'Who is in my team?' },
                      { icon: '📚', text: 'Policies', query: 'Remote work policy?' },
                      { icon: '🎓', text: 'Training', query: 'Available training?' }
                    ].map((suggestion, idx) => (
                      <button
                        key={idx}
                        onClick={() => setInput(suggestion.query)}
                        className={`p-4 ${theme.cardBg} ${theme.textSecondary} ${theme.cardBgHover} ${theme.hoverText} hover:shadow transition-all text-sm font-medium border ${theme.borderLight} hover:border-blue-500`}
                      >
                        <div className="text-2xl mb-2">{suggestion.icon}</div>
                        <div className="text-xs font-bold">{suggestion.text}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`flex items-start space-x-3 max-w-3xl ${msg.type === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                    <div className={`flex-shrink-0 w-10 h-10 flex items-center justify-center border ${
                      msg.type === 'user' 
                        ? 'bg-blue-600 border-blue-500' 
                        : `${theme.cardBg} ${theme.borderLight}`
                    }`}>
                      {msg.type === 'user' ? (
                        <User className="w-5 h-5 text-white" />
                      ) : (
                        <img src="/logo.png" alt="AI" className="w-6 h-6 object-contain" />
                      )}
                    </div>
                    <div
                      className={`px-5 py-3 text-sm border ${
                        msg.type === 'user'
                          ? 'bg-blue-600 text-white border-blue-500'
                          : `${theme.cardBg} ${theme.textSecondary} ${theme.borderLight}`
                      }`}
                    >
                      <p className="whitespace-pre-wrap leading-relaxed">{msg.text}</p>
                    </div>
                  </div>
                </div>
              ))}

              {loading && (
                <div className="flex justify-start">
                  <div className="flex items-start space-x-3 max-w-3xl">
                    <div className={`flex-shrink-0 w-10 h-10 ${theme.cardBg} border ${theme.borderLight} flex items-center justify-center`}>
                      <img src="/logo.png" alt="AI" className="w-6 h-6 object-contain animate-pulse" />
                    </div>
                    <div className={`${theme.cardBg} px-5 py-3 border ${theme.borderLight}`}>
                      <div className="flex space-x-2">
                        <div className="w-2.5 h-2.5 bg-blue-500 animate-bounce"></div>
                        <div className="w-2.5 h-2.5 bg-blue-400 animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-2.5 h-2.5 bg-blue-500 animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Input Area */}
          <div className={`p-6 ${theme.headerBg} border-t ${theme.border}`}>
            <div className="max-w-4xl mx-auto">
              <div className={`flex items-end space-x-3 ${theme.inputBg} p-3 border ${theme.borderLight}`}>
                <div className="flex items-center space-x-1 pb-2">
                  <button
                    onClick={() => showUpgradePopup('File Attachment')}
                    className={`p-2 ${theme.textMuted} ${theme.hoverText} ${theme.hoverBg} transition-all`}
                    title="Attach file"
                  >
                    <Paperclip size={20} />
                  </button>
                  <button
                    onClick={() => showUpgradePopup('Image Upload')}
                    className={`p-2 ${theme.textMuted} ${theme.hoverText} ${theme.hoverBg} transition-all`}
                    title="Upload image"
                  >
                    <ImageIcon size={20} />
                  </button>
                </div>

                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask anything - salary, leave, email, analytics..."
                  disabled={loading}
                  rows="1"
                  className={`flex-1 px-3 py-2 text-sm bg-transparent ${theme.textSecondary} outline-none resize-none disabled:opacity-50 ${isDarkMode ? 'placeholder-gray-600' : 'placeholder-gray-400'}`}
                  style={{ minHeight: '42px', maxHeight: '150px' }}
                />

                <div className="flex items-center space-x-2 pb-2">
                  <button
                    onClick={() => showUpgradePopup('Voice Input')}
                    className={`p-2 ${theme.textMuted} ${theme.hoverText} ${theme.hoverBg} transition-all`}
                    title="Voice input"
                  >
                    <Mic size={20} />
                  </button>
                  <button
                    onClick={handleSend}
                    disabled={loading || !input.trim()}
                    className="flex-shrink-0 bg-blue-600 text-white p-2.5 hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed border border-blue-500"
                  >
                    <Send size={20} />
                  </button>
                </div>
              </div>
              <p className={`text-xs ${theme.textDim} mt-3 text-center font-medium`}>
                TwoRun.AI Enterprise Platform • Ask anything about work, HR, analytics, communication & more
              </p>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
